import React, { useState, useEffect, useRef } from 'react';

function OptimizedImage({ 
  src, 
  alt, 
  className = '', 
  style = {},
  priority = false, // 우선 로드할 이미지인지
  ...props 
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority); // priority가 true면 즉시 로드
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    // priority가 true면 즉시 로드
    if (priority) {
      setIsInView(true);
      // priority 이미지는 미리 fetch하여 캐시에 저장
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = encodeURI(src);
      document.head.appendChild(link);
      return;
    }

    // Intersection Observer로 뷰포트 진입 감지
    if (!isInView && imgRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsInView(true);
              // 이미지가 뷰포트에 들어오면 미리 fetch
              const link = document.createElement('link');
              link.rel = 'prefetch';
              link.href = encodeURI(src);
              document.head.appendChild(link);
              if (observerRef.current) {
                observerRef.current.disconnect();
              }
            }
          });
        },
        {
          rootMargin: '100px', // 뷰포트 100px 전에 미리 로드 (더 빠른 로딩)
          threshold: 0.01
        }
      );

      observerRef.current.observe(imgRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isInView, priority, src]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true); // 에러가 나도 로딩 상태는 해제
  };

  // 이미지가 로드되기 전까지 placeholder 표시
  if (!isInView) {
    return (
      <div 
        ref={imgRef}
        className={`optimized-image-placeholder ${className}`}
        style={{
          ...style,
          backgroundColor: 'rgba(191, 216, 184, 0.1)',
          display: 'inline-block',
        }}
        aria-label={alt}
      />
    );
  }

  return (
    <div 
      className={`optimized-image-wrapper ${className}`}
      style={{ position: 'relative', display: 'inline-block', ...style }}
    >
      {/* 실제 이미지 */}
      <img
        ref={imgRef}
        src={encodeURI(src)}
        alt={alt}
        className={`optimized-image ${isLoaded ? 'loaded' : 'loading'}`}
        onLoad={handleLoad}
        onError={handleError}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        style={{
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          borderRadius: '15px',
          ...style
        }}
        {...props}
      />
      
      {/* 로딩 중 placeholder */}
      {!isLoaded && !hasError && (
        <div 
          className="optimized-image-skeleton"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(191, 216, 184, 0.1)',
            borderRadius: 'inherit'
          }}
        />
      )}
      
      {/* 에러 상태 */}
      {hasError && (
        <div 
          className="optimized-image-error"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(191, 216, 184, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-light)',
            fontSize: '0.875rem'
          }}
        >
          이미지를 불러올 수 없습니다
        </div>
      )}
    </div>
  );
}

export default OptimizedImage;

